import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import { PillPrimary, PillOutline } from '../components/PillButton';
import { api, resolveImage } from '../lib/api';

export const CasiStudioList = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.listCaseStudies().then((d) => { setCaseStudies(d); setLoading(false); }).catch(() => setLoading(false)); }, []);
  return (
    <div className="bg-[#2b2b2b] min-h-screen">
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-[#141414]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="inline-block px-5 py-2.5 rounded-full border border-[#e9dfc0]/40 text-[#e9dfc0] text-[13px] mb-6">Casi Studio</div>
          <h1 className="font-serif text-white text-5xl md:text-7xl leading-[1.05] mb-6">Numeri, non promesse.</h1>
          <p className="text-[#e9dfc0]/80 text-[17px] max-w-2xl leading-relaxed">
            Alcuni dei brand che abbiamo lanciato, gestito o scalato insieme. Ogni caso studio racconta un punto di partenza, azioni concrete e risultati misurabili.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading && <p className="text-[#e9dfc0]/50 col-span-full text-center py-20">Caricamento…</p>}
          {!loading && caseStudies.length === 0 && <p className="text-[#e9dfc0]/50 col-span-full text-center py-20">Nessun caso studio pubblicato.</p>}
          {caseStudies.map((cs) => (
            <Link key={cs.slug} to={`/casi-studio/${cs.slug}`} className="group bg-[#1e1e1e] border border-[#e9dfc0]/15 rounded-2xl overflow-hidden hover:border-[#e9dfc0]/45 transition-colors">
              <div className="aspect-[4/3] bg-cover bg-center relative overflow-hidden">
                <div className="w-full h-full bg-cover bg-center group-hover:scale-105 transition-transform duration-500" style={{ backgroundImage: `url(${resolveImage(cs.image)})` }} />
                <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#2b2b2b]/80 backdrop-blur text-[#e9dfc0] text-[11px]">{cs.tag}</div>
              </div>
              <div className="p-6">
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="font-serif text-[#e9dfc0] text-4xl">{cs.metric}</span>
                  <span className="text-[#e9dfc0]/60 text-sm">in {cs.time}</span>
                </div>
                <h3 className="text-white font-serif text-xl mb-2 leading-snug">{cs.title}</h3>
                <p className="text-[#e9dfc0]/70 text-sm">{cs.excerpt}</p>
                <div className="mt-4 inline-flex items-center gap-2 text-[#e9dfc0] text-sm font-medium group-hover:gap-3 transition-all">Leggi il caso <ArrowRight className="w-4 h-4" /></div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-[#e9dfc0] py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-[#2b2b2b] text-4xl md:text-5xl mb-4">Vuoi essere il prossimo caso studio?</h2>
          <p className="text-[#2b2b2b]/80 mb-8">Prenota una call conoscitiva. 30 minuti per capire se è la mossa giusta per il tuo brand.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/temporary-management#cta" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#2b2b2b] text-[#e9dfc0] text-[15px] font-medium hover:bg-[#1e1e1e] transition-colors">
              Prenota la call <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export const CasoStudioDetail = () => {
  const { slug } = useParams();
  const [cs, setCs] = useState(null);
  const [others, setOthers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // { url, idx }
  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([api.getCaseStudy(slug).catch(() => null), api.listCaseStudies().catch(() => [])])
      .then(([c, all]) => {
        if (!alive) return;
        setCs(c);
        setOthers((all || []).filter((x) => x.slug !== slug).slice(0, 3));
        setLoading(false);
      });
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (!cs || !cs.gallery) return;
      if (e.key === 'ArrowRight') setLightbox((l) => l ? { ...l, idx: (l.idx + 1) % cs.gallery.length, url: cs.gallery[(l.idx + 1) % cs.gallery.length] } : l);
      if (e.key === 'ArrowLeft') setLightbox((l) => l ? { ...l, idx: (l.idx - 1 + cs.gallery.length) % cs.gallery.length, url: cs.gallery[(l.idx - 1 + cs.gallery.length) % cs.gallery.length] } : l);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightbox, cs]);

  if (loading) return <div className="bg-[#2b2b2b] min-h-screen pt-40 pb-24 text-center text-[#e9dfc0]/60">Caricamento…</div>;
  if (!cs) {
    return (
      <div className="bg-[#2b2b2b] min-h-screen pt-40 pb-24 text-center">
        <h1 className="font-serif text-white text-4xl mb-4">Caso studio non trovato</h1>
        <PillPrimary to="/casi-studio">Torna ai casi studio</PillPrimary>
      </div>
    );
  }

  return (
    <div className="bg-[#2b2b2b]">
      <section className="pt-32 pb-10 bg-[#141414]">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
          <Link to="/casi-studio" className="inline-flex items-center gap-2 text-[#e9dfc0]/80 hover:text-white transition-colors text-sm mb-8">
            <ArrowLeft className="w-4 h-4" /> Tutti i casi studio
          </Link>
          <div className="inline-block px-3 py-1 rounded-full bg-[#e9dfc0]/15 text-[#e9dfc0] text-[12px] mb-6">{cs.tag}</div>
          <h1 className="font-serif text-white text-4xl md:text-6xl leading-[1.05] mb-6">{cs.title}</h1>
          <div className="flex flex-wrap gap-6 text-[#e9dfc0]/80 text-sm">
            <div><span className="text-[#e9dfc0]/60">Risultato: </span><span className="text-white font-medium">{cs.metric}</span></div>
            <div><span className="text-[#e9dfc0]/60">Tempo: </span><span className="text-white font-medium">{cs.time}</span></div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
          <div className="aspect-[16/8] bg-cover bg-center rounded-2xl mb-16" style={{ backgroundImage: `url(${resolveImage(cs.image)})` }} />

          {cs.gallery && cs.gallery.length > 0 && (
            <div className="mb-16">
              <h3 className="font-serif text-white text-2xl mb-6">Galleria</h3>
              <div className={`grid gap-5 ${cs.gallery.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                {cs.gallery.map((url, idx) => (
                  <button
                    key={`${idx}-${url.slice(0, 20)}`}
                    type="button"
                    onClick={() => setLightbox({ url, idx })}
                    className="block w-full rounded-xl overflow-hidden border border-[#e9dfc0]/10 hover:border-[#e9dfc0]/40 transition-colors group bg-[#1e1e1e]"
                    aria-label={`Apri immagine ${idx + 1} in dimensione reale`}
                  >
                    <img src={resolveImage(url)} alt={`${cs.title} - ${idx + 1}`} className="w-full h-auto max-h-[520px] object-contain group-hover:scale-[1.02] transition-transform duration-300" loading="lazy" />
                  </button>
                ))}
              </div>
              <p className="text-[#e9dfc0]/40 text-xs mt-3">Clicca su un&apos;immagine per aprirla a schermo intero.</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {cs.results.map((r) => (
              <div key={r.k} className="bg-[#1e1e1e] border border-[#e9dfc0]/15 rounded-xl p-8 text-center">
                <div className="text-[#e9dfc0]/60 text-sm mb-2">{r.k}</div>
                <div className="font-serif text-white text-3xl md:text-4xl">{r.v}</div>
              </div>
            ))}
          </div>

          <div className="space-y-12 text-[#e9dfc0]/90">
            <div>
              <h2 className="font-serif text-white text-3xl mb-4">Il punto di partenza</h2>
              <p className="text-[16px] leading-relaxed">{cs.startPoint}</p>
            </div>
            <div>
              <h2 className="font-serif text-white text-3xl mb-4">Cosa abbiamo fatto</h2>
              <ul className="space-y-3">
                {cs.actions.map((a) => (
                  <li key={a} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#e9dfc0] mt-0.5 flex-shrink-0" />
                    <span className="text-[16px]">{a}</span>
                  </li>
                ))}
              </ul>
            </div>
            {cs.testimonial && (
              <div className="bg-[#3a3a3a] rounded-2xl p-8 md:p-10 border-l-4 border-[#e9dfc0]">
                <p className="font-serif italic text-white text-xl md:text-2xl leading-relaxed">{cs.testimonial}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-[#1e1e1e] py-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <h2 className="font-serif text-white text-3xl mb-10">Altri casi studio</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {others.map((o) => (
              <Link key={o.slug} to={`/casi-studio/${o.slug}`} className="group">
                <div className="aspect-[4/3] bg-cover bg-center rounded-xl mb-4 overflow-hidden">
                  <div className="w-full h-full bg-cover bg-center group-hover:scale-105 transition-transform duration-500" style={{ backgroundImage: `url(${resolveImage(o.image)})` }} />
                </div>
                <h3 className="text-white font-serif text-lg mb-1">{o.title}</h3>
                <p className="text-[#e9dfc0]/60 text-sm">{o.metric} · {o.time}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#e9dfc0] py-16 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h3 className="font-serif text-[#2b2b2b] text-3xl mb-4">Vuoi risultati simili per il tuo brand?</h3>
          <PillOutline to="/temporary-management#cta" className="!bg-[#2b2b2b] !text-[#e9dfc0] !border-[#2b2b2b]">Prenota la call</PillOutline>
        </div>
      </section>

      {lightbox && cs.gallery && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5" />
          </button>
          {cs.gallery.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const next = (lightbox.idx - 1 + cs.gallery.length) % cs.gallery.length;
                  setLightbox({ url: cs.gallery[next], idx: next });
                }}
                className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors text-2xl"
                aria-label="Precedente"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const next = (lightbox.idx + 1) % cs.gallery.length;
                  setLightbox({ url: cs.gallery[next], idx: next });
                }}
                className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors text-2xl"
                aria-label="Successiva"
              >
                ›
              </button>
            </>
          )}
          <img
            src={resolveImage(lightbox.url)}
            alt={`${cs.title} - immagine ${lightbox.idx + 1}`}
            className="max-w-full max-h-full object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          {cs.gallery.length > 1 && (
            <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-white/10 text-white text-sm">
              {lightbox.idx + 1} / {cs.gallery.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
